import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Alert,
  TablePagination
} from '@mui/material';
import { Search as SearchIcon, CheckCircle as CheckIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useManageOrder from './useManageOrder';
import { formatDate } from '../../utils/dateUtils';
import { formatAmount } from '../../utils/formatAmount';

const ReceiveMercari = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState({
    cutoffDate: null,
    currentDate: null,
    totalCount: 0
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Track checked orders (highlighted rows)
  const [checkedOrders, setCheckedOrders] = useState(new Set());
  
  const { getMercariOrderList, markMercariOrderReceived, addMercariCheckRecord, error } = useManageOrder();

  // Use useMemo to filter orders instead of useEffect
  const filteredOrders = useMemo(() => {
    if (searchTerm.trim() === '') {
      return orders;
    } else {
      return orders.filter(order => 
        order.url && order.url.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  }, [orders, searchTerm]);

  // Paginated orders - only show current page
  const paginatedOrders = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, page, rowsPerPage]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Fetch orders on component mount
  useEffect(() => {
    loadOrders();
  }, []);

  // Show error toast when error occurs
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await getMercariOrderList();
      
      if (response && response.success) {
        setOrders(response.data || []);
        setMetadata({
          cutoffDate: response.cutoffDate,
          currentDate: response.currentDate,
          totalCount: response.totalCount || 0
        });
      } else {
        toast.error('Failed to load orders');
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleMarkReceived = async (order) => {
    try {
      const success = await markMercariOrderReceived({orderId: order.id});
      if (success) {
        // Remove the order from the list
        setOrders(prevOrders => prevOrders.filter(o => o.id !== order.id));
        toast.success(`Order ${order.smileNameInput || order.id} marked as received`);
      }
    } catch (error) {
      console.error('Failed to mark order as received:', error);
    }
  };

  const handleOpenLink = async (order) => {
    try {
      // Open the link in new tab
      window.open(order.url, '_blank');
      
      // Add check record
      //await addMercariCheckRecord({orderId: order.id});
      
      // Highlight the row by adding to checked orders
      setCheckedOrders(prev => new Set([...prev, order.id]));
      
      //toast.info(`Link opened and check record added for order: ${order.smileNameInput || order.id}`);
    } catch (error) {
      console.error('Failed to add check record:', error);
      // Still open the link even if check record fails
      window.open(order.url, '_blank');
    }
  };

  const handleRefresh = () => {
    loadOrders();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div style={{ padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          การรับของ Mercari (พัชร)
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRefresh}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          Refresh
        </Button>
      </Box>

      {/* Search Input */}
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by order URL..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon style={{ marginRight: 8, color: 'gray' }} />,
          }}
          size="small"
        />
      </Box>

      {/* Results Count */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          Showing {paginatedOrders.length} of {filteredOrders.length} orders (Page {page + 1} of {Math.ceil(filteredOrders.length / rowsPerPage)})
        </Typography>
        {metadata.totalCount > orders.length && (
          <Chip 
            label={`${metadata.totalCount - orders.length} more orders available`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>URL</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Smile Name</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Tracking Jp</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Shop</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Order Date</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Loading orders...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm ? 'No orders found matching your search.' : 'No orders available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedOrders.map((order, index) => (
                <TableRow 
                  key={order.id || index}
                  hover
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: checkedOrders.has(order.id) ? '#e8f5e8' : 'inherit' // Highlight checked orders
                  }}
                >
                  <TableCell>
                    <a 
                      href={order.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenLink(order);
                      }}
                    >
                      {order.url}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {order.smileNameInput || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {order.trackingJp || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.shop || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.orderDate ? formatDate(order.orderDate) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="success"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkReceived(order);
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
        />
      </TableContainer>
    </div>
  );
};

export default ReceiveMercari;